import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, Plus, Check, Trash2, 
  Move, Settings2, ChevronDown 
} from 'lucide-react';

const CreateMondayTracker = () => {
  const navigate = useNavigate();
  const [trackerData, setTrackerData] = useState({
    name: '',
    description: '',
    columns: [
      { id: 'name', type: 'text', title: 'Item', width: '250px', show: true, required: true },
      { id: 'status', type: 'status', title: 'Status', width: '140px', show: true },
    ],
    statuses: [
      { id: 'not-started', label: 'Not Started', color: '#C4C4C4' },
      { id: 'in-progress', label: 'In Progress', color: '#FDAB3D' },
      { id: 'completed', label: 'Completed', color: '#00C875' },
    ],
    tags: [
      { id: 'important', label: 'Important', color: '#E2445C' },
      { id: 'low-priority', label: 'Low Priority', color: '#579BFC' },
    ]
  });

  const [newStatus, setNewStatus] = useState({ label: '', color: '#00C875' });
  const [newTag, setNewTag] = useState({ label: '', color: '#579BFC' });
  const [newColumn, setNewColumn] = useState({ title: '', type: 'text' });

  const columnTypes = [
    { id: 'text', label: 'Text' },
    { id: 'status', label: 'Status' },
    { id: 'priority', label: 'Priority' },
    { id: 'date', label: 'Date' },
    { id: 'person', label: 'Person' },
    { id: 'tags', label: 'Tags' },
  ];

  const addStatus = () => {
    if (newStatus.label.trim()) {
      setTrackerData(prev => ({
        ...prev,
        statuses: [...prev.statuses, {
          id: newStatus.label.toLowerCase().replace(/\s+/g, '-'),
          ...newStatus
        }]
      }));
      setNewStatus({ label: '', color: '#00C875' });
    }
  };

  const addTag = () => {
    if (newTag.label.trim()) {
      setTrackerData(prev => ({
        ...prev,
        tags: [...prev.tags, {
          id: newTag.label.toLowerCase().replace(/\s+/g, '-'),
          ...newTag
        }]
      }));
      setNewTag({ label: '', color: '#579BFC' });
    }
  };

  const addColumn = () => {
    if (newColumn.title.trim()) {
      setTrackerData(prev => ({
        ...prev,
        columns: [...prev.columns, {
          id: newColumn.title.toLowerCase().replace(/\s+/g, '-'),
          ...newColumn,
          width: '140px',
          show: true
        }]
      }));
      setNewColumn({ title: '', type: 'text' });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Create New Tracker</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/tracker')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white 
              border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // Handle tracker creation
              navigate('/tracker');
            }}
            className="px-4 py-2 text-sm font-medium text-white 
              bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Create Tracker
          </button>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tracker Name
            </label>
            <input
              type="text"
              value={trackerData.name}
              onChange={(e) => setTrackerData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              placeholder="Enter tracker name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={trackerData.description}
              onChange={(e) => setTrackerData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              rows={3}
              placeholder="Enter tracker description"
            />
          </div>
        </div>
      </div>

      {/* Columns Configuration */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-medium text-gray-900">Columns</h2>
        <div className="space-y-4">
          {trackerData.columns.map((column, index) => (
            <div key={column.id} className="flex items-center space-x-3">
              <Move className="w-4 h-4 text-gray-400 cursor-move" />
              <input
                type="text"
                value={column.title}
                onChange={(e) => {
                  const newColumns = [...trackerData.columns];
                  newColumns[index].title = e.target.value;
                  setTrackerData(prev => ({ ...prev, columns: newColumns }));
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                disabled={column.id === 'name'}
              />
              <select
                value={column.type}
                onChange={(e) => {
                  const newColumns = [...trackerData.columns];
                  newColumns[index].type = e.target.value;
                  setTrackerData(prev => ({ ...prev, columns: newColumns }));
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                disabled={column.id === 'name'}
              >
                {columnTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
              {column.id !== 'name' && (
                <button
                  onClick={() => {
                    setTrackerData(prev => ({
                      ...prev,
                      columns: prev.columns.filter((_, i) => i !== index)
                    }));
                  }}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={newColumn.title}
              onChange={(e) => setNewColumn(prev => ({ ...prev, title: e.target.value }))}
              placeholder="New column name"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            <select
              value={newColumn.type}
              onChange={(e) => setNewColumn(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {columnTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
            <button
              onClick={addColumn}
              className="p-2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Statuses Configuration */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-medium text-gray-900">Status Options</h2>
        <div className="space-y-4">
          {trackerData.statuses.map((status, index) => (
            <div key={status.id} className="flex items-center space-x-3">
              <Move className="w-4 h-4 text-gray-400 cursor-move" />
              <input
                type="text"
                value={status.label}
                onChange={(e) => {
                  const newStatuses = [...trackerData.statuses];
                  newStatuses[index].label = e.target.value;
                  setTrackerData(prev => ({ ...prev, statuses: newStatuses }));
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <input
                type="color"
                value={status.color}
                onChange={(e) => {
                  const newStatuses = [...trackerData.statuses];
                  newStatuses[index].color = e.target.value;
                  setTrackerData(prev => ({ ...prev, statuses: newStatuses }));
                }}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <button
                onClick={() => {
                  setTrackerData(prev => ({
                    ...prev,
                    statuses: prev.statuses.filter((_, i) => i !== index)
                  }));
                }}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={newStatus.label}
              onChange={(e) => setNewStatus(prev => ({ ...prev, label: e.target.value }))}
              placeholder="New status"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            <input
              type="color"
              value={newStatus.color}
              onChange={(e) => setNewStatus(prev => ({ ...prev, color: e.target.value }))}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <button
              onClick={addStatus}
              className="p-2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Tags Configuration */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-medium text-gray-900">Tags</h2>
        <div className="space-y-4">
          {trackerData.tags.map((tag, index) => (
            <div key={tag.id} className="flex items-center space-x-3">
              <Move className="w-4 h-4 text-gray-400 cursor-move" />
              <input
                type="text"
                value={tag.label}
                onChange={(e) => {
                  const newTags = [...trackerData.tags];
                  newTags[index].label = e.target.value;
                  setTrackerData(prev => ({ ...prev, tags: newTags }));
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <input
                type="color"
                value={tag.color}
                onChange={(e) => {
                  const newTags = [...trackerData.tags];
                  newTags[index].color = e.target.value;
                  setTrackerData(prev => ({ ...prev, tags: newTags }));
                }}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <button
                onClick={() => {
                  setTrackerData(prev => ({
                    ...prev,
                    tags: prev.tags.filter((_, i) => i !== index)
                  }));
                }}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={newTag.label}
              onChange={(e) => setNewTag(prev => ({ ...prev, label: e.target.value }))}
              placeholder="New tag"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg 
                focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            <input
              type="color"
              value={newTag.color}
              onChange={(e) => setNewTag(prev => ({ ...prev, color: e.target.value }))}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <button
              onClick={addTag}
              className="p-2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMondayTracker; 